import { useEffect, useState, type FormEvent } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import { fromLocalInput, toLocalInput } from '../../lib/datetime'
import {
  CalendarEventTypeLabel,
  type CalendarEventDto,
  type CalendarEventType,
  type CaseListItem,
  type CaseListResponse,
  type UserListItem,
} from '@toga/shared'

interface Props {
  initial: CalendarEventDto | null
  defaultStartAt?: Date
  defaultCaseId?: string | null
  onClose: () => void
  onSaved: () => void
}

export default function EventFormModal({
  initial,
  defaultStartAt,
  defaultCaseId,
  onClose,
  onSaved,
}: Props) {
  const isEdit = initial !== null

  const [title, setTitle] = useState(initial?.title ?? '')
  const [type, setType] = useState<CalendarEventType>(initial?.type ?? 'OTRO')
  const [startAt, setStartAt] = useState(
    initial ? toLocalInput(new Date(initial.startAt)) : toLocalInput(defaultStartAt ?? new Date())
  )
  const [endAt, setEndAt] = useState(
    initial?.endAt
      ? toLocalInput(new Date(initial.endAt))
      : toLocalInput(
          defaultStartAt
            ? new Date(defaultStartAt.getTime() + 60 * 60 * 1000)
            : new Date(Date.now() + 60 * 60 * 1000)
        )
  )
  const [allDay, setAllDay] = useState(initial?.allDay ?? false)
  const [location, setLocation] = useState(initial?.location ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [isPrivate, setIsPrivate] = useState(initial?.isPrivate ?? false)
  const [caseId, setCaseId] = useState(initial?.caseId ?? defaultCaseId ?? '')
  const [attendeeIds, setAttendeeIds] = useState<string[]>(
    initial?.attendees.map((a) => a.userId) ?? []
  )

  const [cases, setCases] = useState<CaseListItem[] | null>(null)
  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [casesResp, usersResp] = await Promise.all([
          api.get<CaseListResponse>('/cases?pageSize=200'),
          api.get<{ users: UserListItem[] }>('/users').catch(() => ({ users: [] })),
        ])
        setCases(casesResp.items)
        setUsers((usersResp as { users: UserListItem[] }).users)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar')
      }
    })()
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        title,
        type,
        startAt: fromLocalInput(startAt).toISOString(),
        endAt: allDay ? null : fromLocalInput(endAt).toISOString(),
        allDay,
        location: location || null,
        description: description || null,
        isPrivate,
        caseId: caseId || null,
        attendeeIds,
      }
      if (isEdit && initial) {
        await api.patch(`/calendar-events/${initial.id}`, payload)
      } else {
        await api.post('/calendar-events', payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const firstFieldErr = body?.issues?.fieldErrors
          ? Object.values(body.issues.fieldErrors).flat().find(Boolean)
          : undefined
        setError(firstFieldErr ?? body?.error ?? 'No se pudo guardar')
      } else {
        setError('Error de red')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async () => {
    if (!initial) return
    if (!confirm('¿Eliminar este evento?')) return
    try {
      await api.delete(`/calendar-events/${initial.id}`)
      onSaved()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar evento' : 'Nuevo evento'}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Título</label>
            <input
              required
              minLength={2}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Tipo</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CalendarEventType)}
              className={inputCls}
            >
              {Object.entries(CalendarEventTypeLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-xs t-muted mb-1.5">Inicio</label>
            <input
              type="datetime-local"
              required
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Fin</label>
            <input
              type="datetime-local"
              disabled={allDay}
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm t-secondary">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          Todo el día
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">Lugar</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Juzgado, despacho, online…"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Expediente (opcional)</label>
            <select value={caseId} onChange={(e) => setCaseId(e.target.value)} className={inputCls}>
              <option value="">Sin expediente</option>
              {cases?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Asistentes (además del creador)</label>
          <select
            multiple
            value={attendeeIds}
            onChange={(e) => setAttendeeIds(Array.from(e.target.selectedOptions, (o) => o.value))}
            size={4}
            className={inputCls}
          >
            {users?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </select>
          <p className="text-xs t-faint mt-1">Ctrl/Cmd + clic para seleccionar varios.</p>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Notas</label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputCls}
          />
        </div>

        <label className="inline-flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
          />
          Privado (solo los asistentes ven el detalle)
        </label>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {isEdit && initial?.canEdit && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-300 hover:bg-rose-500/15 flex items-center gap-1.5"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear evento'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
